// packages/web-ui/src/hooks/useGlobalA11y.ts
//
// W2-S10 Task 4 (AC #4 / #5 / #6) — Global accessibility primitives gathered
// behind a single hook so panels (and tests) have one canonical opt-in.
//
// What this hook provides:
//   1. `announce(message, priority?)` — writes a message to a singleton
//      `aria-live` region attached to the document. Uses `'polite'` by default;
//      `'assertive'` is reserved for hard errors / blocking states.
//   2. `reducedMotion` — re-exports the runtime `prefers-reduced-motion`
//      preference so consumers can branch animations consistently.
//   3. `setReducedMotionCssVar()` — writes `--reduced-motion: 1|0` onto the
//      `<html>` element so plain CSS rules can react without an extra query.
//
// AC #5 says implementation uses a single `useReducedMotion()` hook surfaced
// through a CSS variable + a React context — `useReducedMotion` already exists
// at `../AppShell/useReducedMotion.ts` from W2-S3. This hook delegates to that
// existing source-of-truth (no fork) and adds the CSS variable surface so
// panels in scope can opt in without touching the AppShell.

import { useCallback, useEffect } from 'react';
import { useReducedMotion } from '../AppShell/useReducedMotion.js';

const ANNOUNCE_REGION_ID = 'tessa-a11y-announce-region';
const REDUCED_MOTION_CSS_VAR = '--reduced-motion';

/** ARIA-live priority, mirroring the W3C `aria-live` token set. */
export type AnnouncePriority = 'polite' | 'assertive';

export interface UseGlobalA11yReturn {
  /** Send a one-shot screen-reader announcement. */
  announce: (message: string, priority?: AnnouncePriority) => void;
  /** Current `prefers-reduced-motion: reduce` preference. */
  reducedMotion: boolean;
}

/**
 * Ensure the singleton announcer region exists. Idempotent — only attaches
 * the first call across the page lifetime. No-op outside the browser.
 */
export function ensureAnnouncerRegion(doc: Document = document): HTMLElement | null {
  const existing = doc.getElementById(ANNOUNCE_REGION_ID);
  if (existing !== null) return existing;
  const el = doc.createElement('div');
  el.id = ANNOUNCE_REGION_ID;
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('aria-atomic', 'true');
  // Visually hidden — mirrors the `sr-only` Tailwind utility:
  el.style.position = 'absolute';
  el.style.width = '1px';
  el.style.height = '1px';
  el.style.padding = '0';
  el.style.margin = '-1px';
  el.style.overflow = 'hidden';
  el.style.clip = 'rect(0, 0, 0, 0)';
  el.style.whiteSpace = 'nowrap';
  el.style.border = '0';
  doc.body.appendChild(el);
  return el;
}

/**
 * Free-standing announcer — usable from non-React call-sites (event handlers
 * on the SSE bus, store actions, etc.). React components prefer the
 * `useGlobalA11y().announce` accessor for testability.
 */
export function announceLive(
  message: string,
  priority: AnnouncePriority = 'polite',
  doc: Document = document,
): void {
  const region = ensureAnnouncerRegion(doc);
  if (region === null) return;
  // Update aria-live on the fly so the same region serves both polite and
  // assertive bursts. Browsers re-announce when the live property changes,
  // but we ALSO write text content so screen readers fire even when the
  // priority hasn't shifted.
  region.setAttribute('aria-live', priority);
  // Force a re-announce by clearing first; the setTimeout(0) is the
  // commonly-recommended workaround so the assistive tech notices the
  // text-mutation as a fresh announcement.
  region.textContent = '';
  setTimeout(() => {
    region.textContent = message;
  }, 0);
}

/**
 * React hook bundling the a11y primitives panels need. Mounting this hook
 * once at the top of a panel/component is enough — all consumers share the
 * same singleton announcer region and the same reduced-motion source.
 */
export function useGlobalA11y(): UseGlobalA11yReturn {
  const reducedMotion = useReducedMotion();

  // Keep the `--reduced-motion` CSS var on `<html>` in sync with the runtime
  // preference so panel CSS can use `animation-duration: calc(150ms * (1 - var(--reduced-motion, 0)))`.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.style.setProperty(REDUCED_MOTION_CSS_VAR, reducedMotion ? '1' : '0');
  }, [reducedMotion]);

  // Mount the announcer region once so callers can fire announcements without
  // ordering hazards.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    ensureAnnouncerRegion(document);
  }, []);

  const announce = useCallback((message: string, priority: AnnouncePriority = 'polite') => {
    if (typeof document === 'undefined') return;
    announceLive(message, priority, document);
  }, []);

  return { announce, reducedMotion };
}
